import * as realLinking from 'expo-linking'

import { IS_WEB } from '../constants'

declare global {
  // eslint-disable-next-line @typescript-eslint/no-namespace
  interface Window {
    Cypress: any
    expoLinkingInitialURL?: string | null
    expoLinkingUrlListeners?: Set<(event: { url: string }) => void>
  }
}

let modifiedLinking = realLinking
// The block below stubs the `openURL` method of the expo-linking module.
if (window.Cypress && window.Cypress.runner.getStartTime()) {
  // Need to shallow clone the Linking module because `openURL` property is readonly which prevents it to be stubbed.
  modifiedLinking = { ...realLinking }
  // Guard against re-wrapping when this module is evaluated more than once in
  // a single test runner page (e.g. when a bundler emits the module into two
  // chunks or HMR re-evaluates it). Sinon throws "Attempted to wrap X which
  // is already wrapped" if the property has been stubbed already.
  if (!(modifiedLinking.openURL as { restore?: () => void } | undefined)?.restore) {
    window.Cypress.cy.stub(modifiedLinking, 'openURL').as('openURL')
  }
  if (!(modifiedLinking.getInitialURL as { restore?: () => void } | undefined)?.restore) {
    window.Cypress.cy
      .stub(modifiedLinking, 'getInitialURL')
      .callsFake(() => Promise.resolve(window.expoLinkingInitialURL ?? null))
      .as('getInitialURL')
  }
  // Capture `url` listeners so Cypress can fire deep links through the app's real
  // `Linking.addEventListener` subscription (see `cy.fireExpoDeepLink`).
  if (!(modifiedLinking.addEventListener as { restore?: () => void } | undefined)?.restore) {
    const urlListeners = new Set<(event: { url: string }) => void>()
    window.expoLinkingUrlListeners = urlListeners
    window.Cypress.cy
      .stub(modifiedLinking, 'addEventListener')
      .callsFake((_type: 'url', handler: (event: { url: string }) => void) => {
        urlListeners.add(handler)
        return { remove: () => urlListeners.delete(handler) }
      })
      .as('addEventListener')
  }
}

export const Linking = modifiedLinking

const ensureHttpProtocolInUrl = (url: string) => {
  // Skip relative URLs - they start with /
  if (url.startsWith('/')) {
    return url
  }

  // Leave URLs that already carry a scheme untouched. This covers http(s):// as
  // well as custom schemes like exp+lyraweb-mobile-staging:// or market://.
  // Prepending http:// to an already-schemed URL produces a malformed link
  // (e.g. http://exp+...://...) that the OS hands off to the browser.
  if (/^[a-z][a-z0-9+.-]*:\/\//i.test(url)) {
    return url
  }

  return 'http://' + url
}

export const openUrl = (url: string) => {
  const isPhoneNumber = url.startsWith('tel:')
  const isEmailAddress = url.startsWith('mailto:')
  let validUrl = url.replace(/\s/g, '')

  if (!isPhoneNumber && !isEmailAddress) {
    validUrl = ensureHttpProtocolInUrl(validUrl)
  }

  IS_WEB && !window.Cypress
    ? isPhoneNumber
      ? window.open(validUrl)
      : window.open(validUrl, '_blank')
    : Linking.openURL(validUrl)
}
